import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Bot, User, Trash2, Minimize2, Maximize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const HelpDesk = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  // Generate or retrieve session ID
  useEffect(() => {
    let sid = localStorage.getItem('helpdesk_session_id');
    if (!sid) {
      sid = `helpdesk_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('helpdesk_session_id', sid);
    }
    setSessionId(sid);
  }, []);

  // Load chat history when opened
  useEffect(() => {
    if (isOpen && sessionId) {
      loadHistory();
    }
  }, [isOpen, sessionId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API}/api/helpdesk/history/${sessionId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages);
        } else {
          // Add welcome message if no history
          setMessages([{
            role: 'assistant',
            content: "Hello! I'm the CTC Cargo Network Help Desk Assistant. I can help you with:\n\n• Platform features (RFQs, quotes, messaging)\n• Charter bookings (vessel & cargo flights)\n• Shipping terminology\n• Caribbean shipping information\n\nHow can I assist you today?"
          }]);
        }
      }
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/helpdesk/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });

      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to get response');
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "I'm sorry, I encountered an error. Please try again or contact support if the issue persists." 
        }]);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting. Please check your internet connection and try again." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    if (!window.confirm('Clear chat history?')) return;
    
    try {
      await fetch(`${API}/api/helpdesk/history/${sessionId}`, { method: 'DELETE' });
      setMessages([{
        role: 'assistant',
        content: "Chat history cleared. How can I help you today?"
      }]);
    } catch (error) {
      toast.error('Failed to clear history');
    }
  };

  // Quick action buttons
  const quickActions = [
    { label: 'How to create an RFQ?', message: 'How do I create a Request for Quote (RFQ) on this platform?' },
    { label: 'Charter a vessel', message: 'How can I request a vessel charter?' },
    { label: 'What is DWT?', message: 'What does DWT mean in shipping?' },
    { label: 'Contact support', message: 'How can I contact human support?' }
  ];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-cyan-500 hover:bg-cyan-600 text-white p-4 rounded-full shadow-lg transition-all hover:scale-105"
        data-testid="helpdesk-toggle"
        aria-label="Open Help Desk"
      >
        <MessageCircle className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 right-6 z-50 bg-white rounded-2xl shadow-2xl flex flex-col transition-all ${
        isMinimized ? 'w-80 h-14' : 'w-96 h-[500px]'
      }`}
      data-testid="helpdesk-panel"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 p-2 rounded-full">
            <Bot className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold">Help Desk</h3>
            {!isMinimized && <p className="text-xs text-cyan-100">AI-powered assistance</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            aria-label={isMinimized ? 'Maximize' : 'Minimize'}
          >
            {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            aria-label="Close"
            data-testid="helpdesk-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] p-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-cyan-500 text-white rounded-br-none'
                      : 'bg-slate-100 text-slate-800 rounded-bl-none'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {msg.role === 'assistant' && (
                      <Bot className="h-4 w-4 mt-1 flex-shrink-0 text-cyan-600" />
                    )}
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 p-3 rounded-2xl rounded-bl-none">
                  <div className="flex items-center gap-2">
                    <Bot className="h-4 w-4 text-cyan-600 animate-pulse" />
                    <span className="text-sm text-slate-500">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2">
              <p className="text-xs text-slate-500 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {quickActions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => setInput(action.message)}
                    className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1 rounded-full transition-colors"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 border-t">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question..."
                disabled={loading}
                className="flex-1"
                data-testid="helpdesk-input"
              />
              <Button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-cyan-500 hover:bg-cyan-600"
                data-testid="helpdesk-send"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex justify-between items-center mt-2">
              <button
                type="button"
                onClick={clearHistory}
                className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
              >
                <Trash2 className="h-3 w-3" />
                Clear chat
              </button>
              <span className="text-xs text-slate-400">Powered by AI</span>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default HelpDesk;
